import { useCallback, useRef } from "react";

import { TenantedClientApplicationReviewStateChoices } from "@graphql/generated/graphql.g";
import { NotificationFragment } from "@graphql/notifications/Notifications.g";
import { useUpdateClientApplicationMutation } from "@graphql/client-applications/ClientApplications.g";

import { SlideInPanel } from "@design-system/SlideInPanel/SlideInPanel";

import { ClientApplicationApprovalReviewForm, ClientApplicationApprovalReviewFormValues } from "@components/Forms/ClientApplicationApprovalReviewForm/ClientApplicationApprovalReviewForm";

type Props = {
  notification: NotificationFragment | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

export const ClientApplicationReviewPanel = ({
  notification,
  isOpen,
  onClose,
  onSuccess,
}: Props) => {
  const updateClientApplicationMutation = useUpdateClientApplicationMutation();
  const formRef = useRef<HTMLFormElement>(null);

  const handleFormSubmit = useCallback(
    async (values: ClientApplicationApprovalReviewFormValues) => {
      if (!notification?.application?.id) return;
      
      try {
        await updateClientApplicationMutation.mutateAsync({
          input: {
            id: notification.application.id,
            clientId: values.clientId,
            reviewState: values.reviewState,
            managedBy: values.managedBy,
            supportedBy: values.supportedBy,
            businessOwnerId: values.businessOwnerId,
          },
        });
        onClose();
        onSuccess?.();
      } catch (error) {
        console.error("Failed to update client application:", error);
      }
    },
    [notification, updateClientApplicationMutation, onClose, onSuccess],
  );

  const handleApply = useCallback(() => {
    // Trigger form submission by dispatching a submit event
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  }, []);

  if (!notification) {
    return null;
  }

  return (
    <SlideInPanel
      isOpen={isOpen}
      onClose={onClose}
      title="Application Review"
      onApply={handleApply}
      applyButtonText="Update Application"
    >
      <ClientApplicationApprovalReviewForm
        ref={formRef}
        values={{
          reviewState: notification.application?.reviewState ?? TenantedClientApplicationReviewStateChoices.Discovered,
          managedBy: notification.application?.managedBy ?? undefined,
          supportedBy: notification.application?.supportedBy ?? undefined,
          businessOwnerId: notification.application?.businessOwner?.id ?? undefined,
          clientId: notification.client?.id || "",
        }}
        onSubmit={handleFormSubmit}
        withSubmitButton={false}
      />
    </SlideInPanel>
  );
};